import { Box, Grid, Link, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys, onboardingKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import ZscalerZiaConnectForm from "./ZscalerZiaConnectForm";
import zscaler_zia_step_1 from "./images/zscaler_zia_step_1.png";
import zscaler_zia_step_2 from "./images/zscaler_zia_step_2.png";
import zscaler_zia_step_3 from "./images/zscaler_zia_step_3.png";
import zscaler_zia_step_4 from "./images/zscaler_zia_step_4.png";
import zscaler_zia_step_5 from "./images/zscaler_zia_step_5.png";
import zscaler_zia_step_6 from "./images/zscaler_zia_step_6.png";
import zscaler_zia_step_7 from "./images/zscaler_zia_step_7.png";
import zscaler_zia_step_8 from "./images/zscaler_zia_step_8.png";
import zscaler_zia_step_9 from "./images/zscaler_zia_step_9.png";

const ZscalerZiaIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("zscaler_zia");
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerZscalerZiaIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerZscalerZiaIntegration(
      values.username,
      values.password,
      values.api_key,
      values.baseUrl
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        // only in EDR integrations, we need to invalidate the onboarding context
        queryClient.invalidateQueries({
          queryKey: onboardingKeys.all,
        });
        Toast.success("Zscaler ZIA integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Zscaler ZIA and prioritize the suggested remediations."
            : ""
        }
        image={logos.ZscalerLogo}
        name={"Zscaler ZIA"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Zscaler ZIA is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Add Administrator Role">
                  <Typography variant={"body2"}>
                    Login to ZIA console and navigate to “Administration” →
                    “Authentication” → “Role Management”
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zia_step_1,
                        altext: "Zscaler ZIA Role Management",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Click “Add Administrator Role”.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zia_step_2,
                        altext: "Zscaler ZIA Add Administrator Role",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Name the role “View Only Role” and select all the “View
                    Only” options in each of the permissions.
                    <br />
                    Logs Limit should be set to 180 days. Save the role.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zia_step_3,
                        altext: "Zscaler ZIA new role settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Add Administrator">
                  <Typography variant={"body2"}>
                    Navigate to “Administration” → “Administrator Management”
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zia_step_4,
                        altext: "Zscaler ZIA Administrator Management",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Click “Add Administrator”
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zia_step_5,
                        altext: "Zscaler ZIA “Add Administrator",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Provide the Login ID Name for the user. For example,
                    BreezeAPI@company.com. Assign the View Only <br />
                    role created in the previous step.
                  </Typography>
                  <Typography variant={"body2"}>
                    Enable the password based login and set a password.
                  </Typography>
                  <Typography variant={"body2"}>
                    Click “Save” to complete the process.
                  </Typography>
                  <Typography variant={"body2"}>
                    Note - The password can be highly complex as it is not meant
                    to be used by a human. A password <br />
                    generator is advised.
                  </Typography>
                  {/* TODO - Add a screenshot here when possible. https://linear.app/breeze-security/issue/DEV-1829/add-an-instruction-picture-in-zia-integration-page  */}
                </GuidelineStep>
                <GuidelineStep title="Find Your API Key Base URL">
                  <Typography variant={"body2"}>
                    Navigate to “Administration” → “Cloud Service API Security”
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zia_step_6,
                        altext: "Zscaler ZIA Cloud Service API Security",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    In case the key API exists - copy the key and paste in the
                    form bellow.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zia_step_8,
                        altext: "Zscaler ZIA API key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    If the key is missing select “Add API Key”.
                    <br />
                    Copy the base URL for your API and paste in the form bellow.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zia_step_7,
                        altext: "Zscaler ZIA copy url",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Note - In case the “Add API Key” option is missing - Submit
                    a ticket to Zscaler support requesting the API subscription
                    to be added to your account. Use the “Submit a Ticket” in
                    the Support section within the ZIA console.
                  </Typography>
                  <Typography variant="body2">
                    From the documentation - “You need a valid{" "}
                    <Link
                      href="https://help.zscaler.com/zia/viewing-subscriptions"
                      target="_blank"
                      rel="noopener"
                    >
                      API subscription
                    </Link>{" "}
                    to access and manage your organization’s API key. “
                  </Typography>
                  <Typography variant={"body2"}>
                    Zscaler documentation available at -{" "}
                    <Link
                      href="https://help.zscaler.com/zia/about-cloud-service-api-key"
                      target="_blank"
                      rel="noopener"
                    >
                      https://help.zscaler.com/zia/about-cloud-service-api-key
                    </Link>
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Activate the changes">
                  <Typography variant={"body2"}>
                    Active the changes by navigating to “Activation” and
                    clicking the “Activate” button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: zscaler_zia_step_9,
                        altext: "Zscaler ZIA activation",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the username, password, API key and base URL">
                  <Box width={700}>
                    <ZscalerZiaConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default ZscalerZiaIntegration;
